import { TextInput } from '@components/form/baseInputs'
import { useBrand } from '@contexts/useBrand'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

import DropDown from '../DropDown'
import YearsMonths from '../YearsMonths'

type Address = {
  addressLine1: string
  addressLine2: string
  addressLocality: string
  addressRegion: string
  postalCode: string
}

type Props = {
  prefix?: string
  label?: string
  helper?: string
  optionsLabel?: string
  optional?: boolean
  historic?: boolean
  historicLabel?: string
}

const AddressLookup = ({
  prefix = '',
  label = 'Postcode',
  optional,
  optionsLabel,
  helper,
  historic,
  historicLabel,
}: Props) => {
  const { setValue, getValues, formState } = useFormContext()
  const [suggestions, setSuggestions] = useState<Address[]>([])
  const [manualInput, openManualInput] = useState(true)
  const [searchTerm, setSearchterm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [lookupError, setLookupError] = useState('')
  const brand = useBrand()
  const { errors } = formState

  const handleShowManual = () => {
    openManualInput(!manualInput)
  }

  useEffect(() => {
    if (getValues(`${prefix}postcode`)) {
      openManualInput(true)
    }
  }, [manualInput])

  const handleAddress = (address: Address) => {
    setValue(`${prefix}address_line1`, address.addressLine1, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}address_line2`, address.addressLine2, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}town`, address.addressLocality, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}county`, address.addressRegion, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}postcode`, address.postalCode, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}full_address`, address)
    openManualInput(true)
    setShowSuggestions(false)
  }

  const getAddressList = async () => {
    setIsSearching(true)
    setShowSuggestions(false)
    setLookupError('')
    try {
      const postalCode = encodeURIComponent(searchTerm)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API}/addressLookup?postalCode=${postalCode}`,
      )
      if (!res.ok) throw new Error('Address lookup failed')
      const data: Address[] = await res.json()
      setSuggestions(data)
      if (searchTerm && data.length > 0) {
        setShowSuggestions(true)
        openManualInput(false)
      } else {
        setLookupError('No addresses found for this postcode')
      }
    } catch {
      setLookupError('Could not find addresses. Please enter manually.')
    } finally {
      setIsSearching(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = (e: any) => {
    const index = parseInt(e.target?.value, 10)
    if (!Number.isNaN(index) && suggestions[index]) {
      handleAddress(suggestions[index])
    }
  }

  const suggestionOptions = suggestions.map((address, index) => ({
    value: String(index),
    label: [address.addressLine1, address.addressLine2]
      .filter(Boolean)
      .join(', '),
  }))

  return (
    <>
      <div
        className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-4 items-start justify-top mb-4 md:mb-2"
        data-gt-name="componentevent"
      >
        <div className="md:col-span-2 lg:col-span-1">
          <label className="block">
            <span className="inline-block text-base-text mb-1">
              {label}{' '}
              {optional && (
                <span className="text-xs lowercase whitespace-nowrap">
                  (optional)
                </span>
              )}
            </span>
            <div className="relative grid grid-cols-6">
              <input
                name="places"
                className={twMerge(
                  `rounded-input border-input-border col-span-3 sm:col-span-4 max-w-full mr-4 p-[0.7rem]`,
                )}
                type="text"
                data-gt-action="text-input-interaction"
                value={searchTerm}
                onChange={(e) => setSearchterm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    getAddressList()
                  }
                }}
              />
              <button
                onClick={getAddressList}
                type="button"
                disabled={isSearching}
                className={`relative ${isSearching ? 'opacity-50' : ''} ${
                  brand === 'lexus'
                    ? 'col-span-3 sm:col-span-2 inline-flex items-center justify-center flex-none uppercase !bg-secondary text-white'
                    : 'col-span-3 sm:col-span-2 inline-flex items-center justify-center flex-none rounded-btn px-4 sm:px-6 text-sm sm:text-base !text-white !outline-0 bg-base hover:bg-base-hover active:bg-base-hover focus:bg-base-hover !disabled:cursor-not-allowed disabled:bg-base-hover'
                }`}
                data-gt-name="componentevent"
                data-gt-action="address-lookup-click-cta"
                data-gt-label="find address"
              >
                {isSearching && <div className="loader">Loading...</div>}
                <span className="text-tiny">Find Address</span>
              </button>
            </div>
          </label>
          {lookupError && (
            <p className="text-red text-tiny mt-1" role="alert">
              {lookupError}
            </p>
          )}
          <button
            type="button"
            className="mb-1 col-span-2 md:col-span-1 lg:col-span-2 text-base-text"
            onClick={handleShowManual}
          >
            {manualInput ? 'Find address' : 'Enter address manually'}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-[16px] inline-block ml-2 ${
                manualInput ? 'rotate-180' : ''
              }`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>
        </div>
        <div className="mt-1 md:mt-8 flex items-center md:min-h-[50px]">
          {helper && (
            <p
              className="text-tiny leading-5"
              dangerouslySetInnerHTML={{ __html: helper }}
            />
          )}
        </div>
      </div>

      {showSuggestions && (
        <DropDown
          name={`${prefix}suggestions`}
          options={suggestionOptions}
          optionsLabel={optionsLabel || 'Select your address'}
          onChange={onChange}
        />
      )}

      <div
        className={`${
          !manualInput && !(`${prefix}postcode` in errors) ? 'hidden' : ''
        }`}
      >
        <TextInput name={`${prefix}address_line1`} label="Address Line 1" />
        <TextInput name={`${prefix}address_line2`} label="Address Line 2" />
        <TextInput name={`${prefix}town`} label="Town/City" />
        <TextInput name={`${prefix}county`} label="County" />
        <TextInput name={`${prefix}postcode`} label="Postcode" />
      </div>
      {historic && (
        <YearsMonths historicLabel={historicLabel} prefix={prefix} />
      )}
    </>
  )
}

export default AddressLookup
